import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { gradientHero } from '../../theme';
import PublicNavBar from '../../components/PublicNavBar';
import PortalFooter from '../../components/PortalFooter';

const RegisterCustomerPage: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    phone: '',
    company_name: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await register({
        ...formData,
        role: 'customer',
      });
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <PublicNavBar />

      <Box sx={{ background: gradientHero, color: '#fff', py: 6, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Auftraggeber Registrierung
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Erstellen Sie ein Konto und finden Sie geprüfte Call Center Dienstleister
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          minHeight="50vh"
        >
          <Paper elevation={0} sx={{ p: 5, backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
            <Typography variant="h5" component="h2" mb={4} textAlign="center" sx={{ fontWeight: 'bold' }}>
              Konto erstellen
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
                required
                disabled={isLoading}
                placeholder="Ihr vollständiger Name"
                variant="outlined"
              />

              <TextField
                fullWidth
                label="E-Mail-Adresse"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                required
                disabled={isLoading}
                placeholder="beispiel@beispiel.de"
                variant="outlined"
              />

              <TextField
                fullWidth
                label="Unternehmensname"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                margin="normal"
                disabled={isLoading}
                placeholder="optional"
                variant="outlined"
              />

              <TextField
                fullWidth
                label="Telefon"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                margin="normal"
                disabled={isLoading}
                placeholder="optional"
                variant="outlined"
              />

              <TextField
                fullWidth
                label="Passwort"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                required
                disabled={isLoading}
                variant="outlined"
              />

              <TextField
                fullWidth
                label="Passwort bestätigen"
                name="password_confirmation"
                type="password"
                value={formData.password_confirmation}
                onChange={handleChange}
                margin="normal"
                required
                disabled={isLoading}
                variant="outlined"
              />

              <Button
                fullWidth
                variant="contained"
                type="submit"
                sx={{ mt: 4 }}
                disabled={isLoading}
                size="large"
              >
                {isLoading ? <CircularProgress size={24} /> : 'Registrieren'}
              </Button>
            </form>

            <Stack spacing={2} sx={{ mt: 4 }}>
              <Typography textAlign="center" color="textSecondary">
                Haben Sie bereits ein Konto?{' '}
                <Link to="/login" style={{ textDecoration: 'none', color: '#667eea', fontWeight: 600 }}>
                  Hier anmelden
                </Link>
              </Typography>
              <Typography textAlign="center" color="textSecondary">
                Falsche Kontotyp?{' '}
                <Button
                  component="span"
                  onClick={() => navigate('/register')}
                  sx={{ textDecoration: 'none', color: '#667eea', fontWeight: 600, padding: 0, textTransform: 'none', fontSize: 'inherit' }}
                >
                  Zurück zur Auswahl
                </Button>
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Container>

      <PortalFooter />
    </Box>
  );
};

export default RegisterCustomerPage;
